import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { User } from '../types'

// The refresh token lives in sessionStorage: per tab, gone when the tab
// closes, never in localStorage. That lets a reload refresh past the 30-minute
// access token instead of signing out. "Duplicate tab" copies sessionStorage;
// if both copies refresh, the backend's reuse detection revokes the family and
// both tabs sign out.
export const REFRESH_TOKEN_KEY = 'auth-refresh-token'

const readRefreshToken = (): string | null => {
  try {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY)
  } catch {
    return null
  }
}

const writeRefreshToken = (refreshToken: string | null) => {
  try {
    if (refreshToken) sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
    else sessionStorage.removeItem(REFRESH_TOKEN_KEY)
  } catch {
    // Storage blocked: the token stays in memory for this page only.
  }
}

interface AuthState {
  token: string | null
  refreshToken: string | null
  user: User | null
  setAuth: (token: string, user: User, refreshToken?: string | null) => void
  updateUser: (partial: Partial<User>) => void
  logout: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: readRefreshToken(),
      user: null,
      setAuth: (token, user, refreshToken) =>
        set((state) => {
          const next = refreshToken !== undefined ? refreshToken : state.refreshToken
          writeRefreshToken(next)
          return { token, user, refreshToken: next }
        }),
      updateUser: (partial) =>
        set((state) => ({ user: state.user ? { ...state.user, ...partial } : state.user })),
      logout: () => {
        writeRefreshToken(null)
        set({ token: null, refreshToken: null, user: null })
        useAuthStore.persist.clearStorage()
      },
    }),
    {
      name: 'auth-storage',
      // Keep the long-lived refresh token out of localStorage so it doesn't
      // sit on disk across browser sessions; it is in sessionStorage (above).
      partialize: (state) => ({ token: state.token, user: state.user }),
    }
  )
)
